import {
  selectHasActiveSearchFilters,
  selectSearchDateRange,
  selectSearchTags,
  selectSearchText,
  selectSearchTextMatchingTagIds,
} from "@/store/project-filtering/project-filtering-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { GUID } from "@faro-lotv/foundation";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { useTreeContext } from "../tree-context";
import { useCaptureTreeSelectionStore } from "./capture-tree-selection-store";
import { useCaptureTreeFiltering } from "./use-capture-tree-filtering";

type CaptureTreeContextData = {
  visibleLeaves: number;
  selectedLeaves: GUID[];
  isVisible(id: GUID): boolean;
};

const CaptureTreeContext = createContext<CaptureTreeContextData | undefined>(
  undefined,
);

/** @returns The capture tree context data */
export function useCaptureTreeContext(): CaptureTreeContextData {
  const context = useContext(CaptureTreeContext);
  if (!context) {
    throw new Error("CaptureTreeContext is not initialized.");
  }
  return context;
}

type TreeDataWithParents = TreeData & {
  parent: TreeDataWithParents | null;
  children: TreeDataWithParents[] | null;
  level: number;
};

/**
 * @returns The capture tree context provider
 */
export function CaptureTreeContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { selectedIds, updateSelection } = useCaptureTreeSelectionStore();
  const [visibleLeaves, setVisibleLeaves] = useState(0);
  const [selectedLeaves, setSelectedLeaves] = useState<GUID[]>([]);

  const { tree } = useTreeContext();

  const hasActiveFilters = useAppSelector(selectHasActiveSearchFilters);
  const searchText = useAppSelector(selectSearchText);
  const filteringDateRange = useAppSelector(selectSearchDateRange);
  const filteringTags = useAppSelector(selectSearchTags);
  const textTagIds = useAppSelector(selectSearchTextMatchingTagIds);

  const filter = useCaptureTreeFiltering({
    hasActiveFilters,
    searchText,
    filteringDateRange,
    filteringTags,
    textTagIds,
  });

  const treeWithParents = useMemo(() => {
    function updateTree(
      nodes: TreeData[],
      parent: TreeDataWithParents | null,
    ): TreeDataWithParents[] {
      const data: TreeDataWithParents[] = [];
      for (const node of nodes) {
        const newData: TreeDataWithParents = {
          ...node,
          parent,
          children: null,
          level: parent ? parent.level + 1 : 0,
        };
        if (node.children) {
          newData.children = updateTree(node.children, newData);
        }
        data.push(newData);
      }
      return data;
    }
    return updateTree(tree, null);
  }, [tree]);

  const nodes = useMemo(() => {
    const nodes: Record<
      GUID,
      (TreeDataWithParents & { isVisible: boolean }) | undefined
    > = {};
    const stack = [...treeWithParents];

    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) continue;

      const nodeWithVisibility = { ...node, isVisible: false };
      nodes[node.id] = nodeWithVisibility;

      if (
        filter({
          level: node.level,
          data: node,
          parent: node.parent,
        })
      ) {
        nodeWithVisibility.isVisible = true;

        // If a node is visible, we flag as visible all its ancestors as well.
        let { parent: parentNode } = node;
        while (parentNode) {
          const parentWithVisibility = nodes[parentNode.id];
          if (parentWithVisibility) {
            parentWithVisibility.isVisible = true;
          }
          parentNode = parentNode.parent;
        }
      }

      if (node.children) {
        stack.push(...node.children);
      }
    }
    return nodes;
  }, [filter, treeWithParents]);

  const isVisible = useCallback((id: GUID) => !!nodes[id]?.isVisible, [nodes]);

  // Every time the tree changes (filtering, elements moved around), we
  // recompute the selection state starting from the selected leaves that are still visible
  const recomputeSelectionFromLeaves = useCallback(
    (selectedIds: Set<GUID>) => {
      // Start from an empty selection
      const currentSelection = new Set<GUID>();

      for (const selectedNode of [...selectedIds]) {
        const node = nodes[selectedNode];
        // If an element is not visible anymore or is not a leaf, skip it
        if (!node || !nodes[selectedNode]?.isVisible || node.children?.length) {
          continue;
        }

        // Select the current visible leaf node
        currentSelection.add(node.id);

        // Select all parents
        let parentNode = node.parent;
        while (parentNode) {
          currentSelection.add(parentNode.id);
          parentNode = parentNode.parent;
        }
      }

      return currentSelection;
    },
    [nodes],
  );

  useEffect(() => {
    updateSelection(recomputeSelectionFromLeaves);
  }, [recomputeSelectionFromLeaves, updateSelection]);

  // Update the stats when the selection or visibility changes
  useEffect(() => {
    let visibleLeaves = 0;
    const selectedLeaves: GUID[] = [];
    for (const node of Object.values(nodes)) {
      if (node && node.isVisible && !node.children?.length) {
        visibleLeaves++;
        if (selectedIds.has(node.id)) {
          selectedLeaves.push(node.id);
        }
      }
    }
    setVisibleLeaves(visibleLeaves);
    setSelectedLeaves(selectedLeaves);
  }, [selectedIds, nodes]);

  const value = useMemo(
    () => ({
      visibleLeaves,
      selectedLeaves,
      isVisible,
    }),
    [visibleLeaves, selectedLeaves, isVisible],
  );

  return (
    <CaptureTreeContext.Provider value={value}>
      {children}
    </CaptureTreeContext.Provider>
  );
}
